import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Post } from '../../types/post';

interface PaginationState {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

interface PostState {
  items: Post[];
  currentPost: Post | null;
  pagination: PaginationState;
  filters: {
    category: string | null;
    searchIntent: string | null;
    location: { lat: number; lon: number; radiusKm: number } | null;
    dateTime: { fromDate: string | null; toDate: string | null } | null;
  };
  loading: boolean;
  error: string | null;
}

const initialState: PostState = {
  items: [],
  currentPost: null,
  pagination: {
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
  },
  filters: {
    category: null,
    searchIntent: null,
    location: null,
    dateTime: null,
  },
  loading: false,
  error: null,
};

const postSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    setPosts: (state, action: PayloadAction<Post[]>) => {
      state.items = action.payload;
    },
    setCurrentPost: (state, action: PayloadAction<Post | null>) => {
      state.currentPost = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaginationState>) => {
      state.pagination = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<PostState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        category: null,
        searchIntent: null,
        location: null,
        dateTime: null,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addPost: (state, action: PayloadAction<Post>) => {
      state.items.unshift(action.payload);
    },
    updatePost: (state, action: PayloadAction<Post>) => {
      const index = state.items.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentPost?.id === action.payload.id) {
        state.currentPost = action.payload;
      }
    },
    removePost: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((p) => p.id !== action.payload);
      if (state.currentPost?.id === action.payload) {
        state.currentPost = null;
      }
    },
  },
});

export const {
  setPosts,
  setCurrentPost,
  setPagination,
  setFilters,
  clearFilters,
  setLoading,
  setError,
  addPost,
  updatePost,
  removePost,
} = postSlice.actions;
export default postSlice.reducer;

